import React from 'react';
import { BannedPost, ModerationDecision } from 'services/api/banInfo/types';
import { formatDateTime } from './helpers';

interface Props {
  type: 'log' | 'post';
  data: ModerationDecision | BannedPost | null;
  loading: boolean;
  error: string;
  mediaUrl: string;
  mediaType: string;
  mediaLoading: boolean;
  onLoadMedia: () => void;
  onClose: () => void;
}

const ModalRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="blocked-inspection__row">
    <span>{label}</span>
    <strong>{value == null || value === '' ? 'Not provided' : value}</strong>
  </div>
);

export const BlockedInspectionModal: React.FC<Props> = ({
  type,
  data,
  loading,
  error,
  mediaUrl,
  mediaType,
  mediaLoading,
  onLoadMedia,
  onClose,
}) => {
  const log = type === 'log' ? (data as ModerationDecision | null) : null;
  const post = type === 'post' ? (data as BannedPost | null) : null;
  const directMediaUrl = post?.media?.url || '';
  const previewUrl = mediaUrl || directMediaUrl;
  const isVideo =
    mediaType.startsWith('video/') ||
    /\.(mp4|webm|mov)(\?|$)/i.test(previewUrl);

  return (
    <div
      className="blocked-inspection"
      role="dialog"
      aria-modal="true"
      onMouseDown={onClose}
    >
      <div
        className="blocked-inspection__card"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="blocked-inspection__header">
          <div>
            <span>
              {type === 'log' ? 'Moderation decision' : 'Blocked content'}
            </span>
            <h2>
              {type === 'log'
                ? `Decision #${log?.id || ''}`
                : `Content #${post?.id || ''}`}
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close details">
            ×
          </button>
        </header>

        <div className="blocked-inspection__body">
          {loading ? (
            <div className="blocked-inspection__loading">
              <span />
              <span />
              <span />
              <span />
            </div>
          ) : null}

          {!loading && error ? (
            <div className="blocked-inspection__error">
              <strong>Could not load full details</strong>
              <span>{error}</span>
            </div>
          ) : null}

          {!loading && log ? (
            <>
              <div className="blocked-inspection__badge-row">
                <span className="blocked-inspection__badge">
                  {log.category || log.judge_verdict || 'Moderation'}
                </span>
                <time>{formatDateTime(log.created_at)}</time>
              </div>
              <section className="blocked-inspection__section">
                <h3>Decision information</h3>
                <ModalRow label="Action tier" value={log.action_tier} />
                <ModalRow label="Category" value={log.category} />
                <ModalRow label="Judge verdict" value={log.judge_verdict} />
                <ModalRow label="Reason" value={log.reason_text} />
                <ModalRow label="Restriction" value={log.restriction} />
                <ModalRow label="Detector score" value={log.detector_score} />
                <ModalRow
                  label="Judge confidence"
                  value={log.judge_confidence}
                />
              </section>
              <section className="blocked-inspection__section">
                <h3>References</h3>
                <ModalRow
                  label="User ID"
                  value={log.user_id ? `#${log.user_id}` : ''}
                />
                <ModalRow
                  label="Channel ID"
                  value={log.channel_id ? `#${log.channel_id}` : ''}
                />
                <ModalRow
                  label="Group ID"
                  value={log.group_id ? `#${log.group_id}` : ''}
                />
                <ModalRow
                  label="Peer ID"
                  value={log.peer_id ? `#${log.peer_id}` : ''}
                />
                <ModalRow
                  label="Post ID"
                  value={log.post_id ? `#${log.post_id}` : ''}
                />
                <ModalRow
                  label="Message ID"
                  value={log.message_id ? `#${log.message_id}` : ''}
                />
              </section>
            </>
          ) : null}

          {!loading && post ? (
            <>
              <div className="blocked-inspection__badge-row">
                <span className="blocked-inspection__badge">
                  {post.restriction_category ||
                    post.judge_category_name ||
                    'Blocked content'}
                </span>
                <time>{formatDateTime(post.created_at)}</time>
              </div>

              <section className="blocked-inspection__section">
                <h3>Moderation result</h3>
                <ModalRow label="Reason" value={post.reason_text} />
                <ModalRow label="Judge verdict" value={post.judge_verdict} />
                <ModalRow label="Restriction" value={post.restriction} />
                <ModalRow
                  label="Moderation status"
                  value={post.moderation_status}
                />
                <ModalRow label="Detector score" value={post.detector_score} />
                <ModalRow
                  label="Judge confidence"
                  value={post.judge_confidence}
                />
              </section>

              <section className="blocked-inspection__section">
                <h3>References</h3>
                <ModalRow
                  label="User ID"
                  value={post.user_id ? `#${post.user_id}` : ''}
                />
                <ModalRow
                  label="Channel ID"
                  value={post.channel_id ? `#${post.channel_id}` : ''}
                />
                <ModalRow
                  label="Message ID"
                  value={post.message_id ? `#${post.message_id}` : ''}
                />
                <ModalRow
                  label="Peer ID"
                  value={post.peer_id ? `#${post.peer_id}` : ''}
                />
              </section>

              {post.media ? (
                <section className="blocked-inspection__section">
                  <div className="blocked-inspection__media-heading">
                    <h3>Media</h3>
                    {!mediaUrl ? (
                      <button
                        type="button"
                        onClick={onLoadMedia}
                        disabled={mediaLoading}
                      >
                        {mediaLoading ? 'Loading…' : 'Load secured media'}
                      </button>
                    ) : null}
                  </div>
                  {previewUrl ? (
                    <div className="blocked-inspection__media">
                      {isVideo ? (
                        <video
                          src={previewUrl}
                          controls
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <img src={previewUrl} alt="Blocked post media" />
                      )}
                    </div>
                  ) : (
                    <div className="blocked-inspection__media-placeholder">
                      Media is stored securely. Use the button above to stream
                      it.
                    </div>
                  )}
                </section>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
